import type { ComponentChildren } from 'preact';
import type { HijriDay } from '../astro/hijri';
import { keepHonorifics } from '../data/islam';
import { eclipseHadith } from '../live/skycalendar';
import { settings } from '../state';
import { openIslam } from './IslamPanel';
import { Icon } from './icons';
import { Quotes } from './Quote';
import { SayArabic } from './SayArabic';

// The approved Islamic texts where the sky calls for them (BUILD-SPEC items 4 to 9, 36 and 37): a short note with the
// texts word for word, those beyond the first behind a tap so the card stays short.

/** `lead`: Ahillah's own line before the texts; `children`: anything after them. */
export function IslamNote({ title, icon = 'moon', lead, shown = [], more, link, children }: { title: string; icon?: string; lead?: ComponentChildren; shown?: string[]; more?: { label: string; ids: string[] }; link?: { label: string; section: string }; children?: ComponentChildren }) {
  return (
    <aside class="reminder islam-note" aria-label={title}>
      <div class="reminder-head"><Icon name={icon} size={15} /> {title}</div>
      {lead}
      <Quotes ids={shown} />
      {children}
      {more && (
        <details class="islam-more">
          <summary>{more.label}</summary>
          <Quotes ids={more.ids} />
        </details>
      )}
      {link && <button class="reminder-more" onClick={() => openIslam(link.section)}>{link.label} <Icon name="chevron" size={13} /></button>}
    </aside>
  );
}

/** From the 10th to the 12th: the white days are near. In Dhū al-Ḥijjah, Ibn Bāz's answer instead, so the note never
 *  invites fasting on the 13th, a day of al-Tashrīq (items 7 and 36). None in Ramaḍān, whose every day is fasted. */
export function WhiteDaysNote({ h }: { h: HijriDay }) {
  if (h.day < 10 || h.day > 12 || h.month === 9) return null;
  if (h.month === 12) return <IslamNote title="The white days" shown={['SC-22']} />;
  return (
    <IslamNote title="The white days" lead={<p>The white days are the 13th, 14th and 15th.</p>} more={{ label: 'The hadith, and why they are called white', ids: ['H-17', 'H-18', 'SC-20'] }} />
  );
}

/** On the night of the full Moon: the hadith of seeing the Moon, with both scholars' explanations of the comparison
 *  (items 8, 9 and 37). */
export const FullMoonNote = () => <IslamNote title="The full Moon" shown={['H-19']} more={{ label: 'The scholars’ explanation', ids: ['SC-23', 'SC-24'] }} />;

/** The Prophet's words on eclipses, wherever an eclipse can be seen (not the faint penumbral kind: items 21 and 22),
 *  with Ibn ʿUthaymīn's and Ibn Bāz's words on praying it: for when it is seen (item 23). */
export function EclipsePrayer({ solar }: { solar: boolean }) {
  const h = eclipseHadith();
  return (
    <aside class="reminder cal-prayer" aria-label="The eclipse prayer">
      <div class="reminder-head"><Icon name="info" size={15} /> The eclipse prayer ({solar ? 'ṣalāt al-kusūf' : 'ṣalāt al-khusūf'})</div>
      <p>{keepHonorifics('The Prophet ﷺ said:')}</p>
      {settings.value.arabic && <p class="islam-ar" lang="ar" dir="rtl">{h.arabic}</p>}
      {settings.value.arabic && <SayArabic text={h.arabic} />}
      <p>{h.text}</p>
      <p class="cal-ref">{h.ref}</p>
      <details class="islam-more">
        <summary>Ibn ʿUthaymīn ﵀ and Ibn Bāz ﵀ on praying it</summary>
        <Quotes ids={['SC-32', 'SC-34']} />
      </details>
      <button class="reminder-more" onClick={() => openIslam()}>The stars in Islam <Icon name="chevron" size={13} /></button>
    </aside>
  );
}

/** A verse where the sky calls for it, with no explanation (item 43, the owner's choice of 22 October 2026): on the Sun
 *  card [Fuṣṣilat: 41:37], and on the Moon card [Al-Baqarah: 2:189]. */
export const VerseNote = ({ id }: { id: string }) => <IslamNote title="In the Quran" icon="book" shown={[id]} />;

/** On a meteor shower's card: the verse with Ibn Kathīr's short explanation, and the way to the article (item 14). */
export const ShootingStarsNote = () => (
  <IslamNote title="Shooting stars in Islam" icon="meteor" shown={['Q-40', 'SC-25']} link={{ label: 'Read the article', section: 'shooting-stars' }} />
);

/** On the card of the Pleiades, al-Thurayyā: the hadith that name it (item 31), in the owner's order: Muslim 2546, its
 *  other narration, Ibn Taymiyyah's explanation of who the men are, then the rulers and careless speech. */
export const PleiadesNote = () => (
  <IslamNote title="Al-Thurayyā in the hadith" icon="star" shown={['H-24']}>
    <details class="islam-more">
      <summary>Another narration, Ibn Taymiyyah ﵀, and two more hadith</summary>
      <p class="islam-label">Another narration</p>
      <Quotes ids={['H-25', 'SC-44', 'H-22', 'H-26']} />
    </details>
  </IslamNote>
);

/** ʿArafah and ʿĀshūrāʾ in the sky calendar (DECISIONS item 79): the hadith, then the other and Ibn ʿUthaymīn's words. */
export const DayNote = ({ day }: { day: 'arafah' | 'ashura' }) =>
  day === 'arafah' ? (
    <IslamNote title="The day of ʿArafah" icon="calendar" shown={['H-37']} more={{ label: 'Another hadith, and Ibn ʿUthaymīn ﵀ on fasting it', ids: ['H-40', 'SC-45'] }} />
  ) : (
    <IslamNote title="The day of ʿĀshūrāʾ" icon="calendar" shown={['H-38']} more={{ label: 'Another hadith, and Ibn ʿUthaymīn ﵀ on fasting it', ids: ['H-39', 'SC-46'] }} />
  );
